import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import './scan.css';
import TongueTemplate from '../images/tongue_template_transparent.png';
import HistoryIcon from '../images/history_icon.png';
import ImageUploadIcon from '../images/image_icon.png';
import { Button, Image, message, Spin } from "antd";
let stream: any | null = null;
export default function TongueScan() {
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream>(null);
  const [isFrontCamera, setIsFrontCamera] = useState(true);

  // 初始化摄像头
  const initCamera = async (isFront: boolean) => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: isFront ? "user" : "environment" } // 切换前后置摄像头
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        mediaStreamRef.current = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error('摄像头访问失败:', error);
      message.error('摄像头访问失败，请检查设备权限');
    }
  };

  const checkCameraPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      initCamera(isFrontCamera);
    } catch (error) {
      console.error('摄像头访问失败:', error);
      message.error('摄像头访问失败，请检查设备权限');
    }
  };

  useEffect(() => {
    checkCameraPermission();
    return () => {
      mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, [isFrontCamera]);

  // 翻转摄像头
  const toggleCamera = () => {
    setIsFrontCamera((prev) => !prev);
  };

  // 拍摄功能
  const captureImage = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    const video = videoRef.current;
    // 前置摄像头时需要放大1.2倍，后置摄像头时需要放大1.5倍
    if (isFrontCamera) {
      canvas.width = video.videoWidth * 1.2;
      canvas.height = video.videoHeight * 1.2;
    } else {
      canvas.width = video.videoWidth * 1.5;
      canvas.height = video.videoHeight * 1.5;
    }

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // 前置摄像头时需要水平翻转
      if (isFrontCamera) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL('image/png');
      handleUpload(imageUrl);
    }

    video.pause();
  };

  // 上传功能
  const handleUpload = async (capturedImage: any, filename?: string) => {
    if (!capturedImage) return;

    try {
      setIsUploading(true);
      const blob = await fetch(capturedImage).then(r => r.blob());
      const formData = new FormData();
      const finalFilename = filename || `tongue_${Date.now()}.png`;
      formData.append('file', blob, finalFilename);

      const response = await fetch(`${window.BASE_URL}/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      if (result.error) {
        videoRef.current?.play();
        message.error(`上传失败: ${result.error}`);
        return;
      } else {
        navigate(`/detail`, {
          state: {
            analysisResult: result
          }
        });
        message.success('上传成功');
      }
    } catch (error) {
      videoRef.current?.play();
      console.error('上传失败:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Spin spinning={isUploading}>
      <div className="tongue-scan">
        {/* 视频元素用于显示摄像头画面 */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            backgroundColor: 'black',
            transform: `scale(1.5) ${isFrontCamera ? 'scale(1.2) scaleX(-1)' : ''}`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease'
          }}
        />
        {/* 顶部提示区 */}
        <div className="scan-tip">
          <p style={{ color: 'white', fontSize: '16px' }}>请拍摄舌面</p>
          <p style={{ color: 'white', fontSize: '12px', margin: 0 }}>舌体放松，舌面平展，舌尖略向下，口张大不要太用力</p>
        </div>

        {/* 中间舌诊模板区 */}
        <div className="camera-overlay">
          <Image
            src={TongueTemplate}
            className="tongue-template"
            style={{
              width: '60%',
              pointerEvents: 'none',
              margin: '0 20%',
            }}
            loading="lazy"
            preview={false}
          />
        </div>

        {/* 底部功能区 */}
        <div className="camera-controls">
          <div className="flip-btn" onClick={toggleCamera}>翻转相机</div>
          <div className="camera-buttons-row">
            <label className="control-btn upload-btn">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setIsUploading(true);
                    const imageUrl = URL.createObjectURL(file);
                    await handleUpload(imageUrl, file.name);
                    setIsUploading(false);
                  }
                }}
              />
              <Image src={ImageUploadIcon} alt="上传" preview={false} loading="lazy" />
              相册上传
            </label>
            <Button className="shutter-btn" disabled={isUploading} onClick={captureImage} // 绑定拍摄功能
            />
            <label className="control-btn history-btn" onClick={() => navigate('/list')}>
              <Image src={HistoryIcon} alt="舌诊记录" preview={false} loading="lazy" />
              舌诊记录
            </label>
          </div>
        </div>
      </div>
    </Spin>
  );
}